import { HttpStatusCode } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { IAuthService } from '@app/auth/interfaces/auth-service.interface';
import { StorageKey, StorageService } from '@avenir-client-web/storage';
import { CredentialsService } from '@core/authentication/credentials.service';
import { AppRootRoute } from '@core/enums/app-route.enum';
import { Credentials } from '@core/models/credentials.model';
import { PermissionService } from '@core/services/permission.service';
import { ProfileService } from '@core/services/profile.service';
import { OKTA_AUTH } from '@okta/okta-angular';
import { AuthState, OktaAuth } from '@okta/okta-auth-js';
import {
  BehaviorSubject,
  catchError,
  filter,
  first,
  forkJoin,
  map,
  Observable,
  Subject,
  takeUntil,
  throwError,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthOktaService implements IAuthService, OnDestroy {
  loginSuccess$: Observable<void>;

  private readonly loginSuccessSubject$ = new BehaviorSubject<boolean>(false);

  private readonly compUnsubscribe$ = new Subject<void>();

  private isLoggingOut = false;

  constructor(
    @Inject(OKTA_AUTH) private readonly oktaAuth: OktaAuth,
    private readonly credentialsService: CredentialsService,
    private readonly permissionService: PermissionService,
    private readonly profileService: ProfileService,
    private readonly router: Router,
    private readonly storage: StorageService
  ) {
    this.loginSuccess$ = this.loginSuccessSubject$.asObservable().pipe(
      filter(Boolean),
      map(() => undefined),
      first()
    );
  }

  ngOnDestroy(): void {
    this.compUnsubscribe$.next();
    this.compUnsubscribe$.complete();
  }

  loginRedirect(): void {
    this.oktaAuth.signInWithRedirect();
  }

  async logout(): Promise<void> {
    try {
      // Set flag to prevent auto-redirect during logout
      this.isLoggingOut = true;

      // Clear credentials and profile
      this.credentialsService.removeCredentials();
      this.profileService.onLogout();

      // Sign out from Okta (this will redirect to Okta's logout page and back)
      await this.oktaAuth.signOut();
    } catch (error) {
      console.error('Error during logout:', error);
      // Reset flag even if logout fails
      this.isLoggingOut = false;
    }
  }

  init(): void {
    this.handleCallback();
    this.listenOktaAuthStateChanges();
  }

  handleAfterLoggedIn(): void {
    this.handleAuthentication(this.oktaAuth.authStateManager.getAuthState());
  }

  private async handleCallback(): Promise<void> {
    try {
      // Check if we're returning from login (code and state in URL)
      if (this.oktaAuth.isLoginRedirect()) {
        await this.oktaAuth.handleLoginRedirect();
        // After handling redirect, update auth state
        await this.oktaAuth.authStateManager.updateAuthState();
      }
    } catch (error) {
      console.error('Error handling login redirect:', error);
      // Clear any invalid state and redirect to login
      await this.oktaAuth.clearStorage();
      this.loginRedirect();
    }
  }

  private listenOktaAuthStateChanges(): void {
    // Subscribe to authState changes
    this.oktaAuth.authStateManager.subscribe((authState: AuthState) => {
      // Skip auth state handling during logout
      if (this.isLoggingOut) {
        return;
      }

      if (!authState.isAuthenticated) {
        // User is not authenticated, redirect to login
        if (
          this.router.url !== `/${AppRootRoute.SIGN_IN}` &&
          !this.oktaAuth.isLoginRedirect()
        ) {
          this.loginRedirect();
        }
      } else {
        // User is authenticated
        this.handleAuthentication(authState);
      }
    });

    // Start the auth state update
    this.oktaAuth.start();
  }

  private async handleAuthentication(authState: AuthState): Promise<void> {
    try {
      // Get access token and store it
      const accessToken = authState.accessToken?.accessToken;

      if (accessToken) {
        this.storage.set(StorageKey.ACCESS_TOKEN, accessToken);
      }

      // Check if this is a successful login (first time authentication)
      if (authState.isAuthenticated && !this.loginSuccessSubject$.getValue()) {
        this.loginSuccessSubject$.next(true);
      }

      // Handle user profile and permissions
      if (this.profileService.getUserActiveState()) {
        this.permissionService
          .loadPermissions()
          .pipe(takeUntil(this.compUnsubscribe$))
          .subscribe();

        forkJoin([
          this.permissionService.getUserPermissions(),
          this.profileService.getProfile(),
        ])
          .pipe(
            catchError(error => {
              if (error.status === HttpStatusCode.Forbidden) {
                this.logout();
              }

              return throwError(() => error);
            })
          )
          .subscribe(([permissions, profile]) => {
            const user = authState.idToken?.claims;

            if (user) {
              const defaultUserPermission =
                permissions.find(permission => permission.role.isCurrent) ??
                permissions.find(permission => permission.role.isDefault) ??
                permissions[0];

              this.credentialsService.setCredentials({
                id: profile.id,
                externalId: profile.externalId,
                role: defaultUserPermission.role,
                email: (user.email as string) || profile.email,
                userName:
                  (user.preferred_username as string) || profile.userName,
                fullName: (user.name as string) || profile.name,
                preferredLanguage: profile.preferredLanguage,
              } as Credentials);
            }
          });
      } else {
        this.router.navigate([AppRootRoute.SIGN_IN]);
      }
    } catch (error) {
      console.error('Error handling authentication:', error);
      this.loginRedirect();
    }
  }
}
